import { Directive, ElementRef, Input } from '@angular/core';
import { environment } from 'src/environments/environment';

/*
 * Massively inspired by https://codepen.io/vsync/pen/frudD
 * and https://github.com/angular/material/blob/master/src/components/input/input.js
 *
 * Note that there must be an [ngModel] attribute on the textarea to trigger the updates
 */
@Directive({
  selector: '[appTextareaAutoExpand]',
})
export class TextareaAutoExpandDirective {
  private MAX_ROWS = environment.bundledWithAngularJSApp ? 8 : 5; // Hardcode it for now, as we only have one textarea in the app anyway

  private lineHeight = null;
  ta: HTMLTextAreaElement;

  @Input() scrollContainer: HTMLElement;
  @Input() shouldAdjustPageScroll: boolean;

  constructor(elRef: ElementRef) {
    this.ta = elRef.nativeElement;
    this.ta.rows = 1;
  }

  @Input() set ngModel(value) {
    if (this.canUpdateRows()) {
      this.updateRows();
    }
  }

  // not private because single-message-editor calls this method to update the rows after a favorite is selected
  // (the ngModel above is called before the native texarea's value is updated, and calling updateRows above in a setTimeout()
  // would make the update look visually not fluid, for example a scroll bar would flash in desktop mode)
  updateRows() {
    let rowsBefore: number;
    let scrollBefore: number;
    if (this.isPageScrollContainerAvailable()) {
      // The ngModel setter above is called before the containingPageElement input is set
      rowsBefore = this.ta.rows;
      scrollBefore = this.scrollContainer.scrollTop;
    }
    this.ta.rows = 1;
    this.ta.style.minHeight = '0';
    this.updateLineHeightIfNull();
    // clientHeight is 1 line + padding, scrollHeight is all lines + padding
    const lines = 1 + Math.round((this.ta.scrollHeight - this.ta.clientHeight) / this.lineHeight);
    this.ta.rows = Math.min(lines, this.MAX_ROWS);
    this.ta.style.minHeight = '';

    if (!environment.bundledWithAngularJSApp && this.shouldAdjustPageScroll && this.isPageScrollContainerAvailable()) {
      // adjust page scroll so that it looks like the expanding textarea is pushing the page content
      // (but we don't do that for twitter public because there can be several textareas in the editor,
      //  and they all call updateRows everytime they gain focus,
      //  and it would be a mess to track if another textarea was focused when we get here)
      // We don't do it either in desktop mode, as the editor has a min-height to be aligned with the "send" and "send and..." buttons,
      // and it would be too messy for little UX gain to make it work in that case.
      const deltaRows = this.ta.rows - rowsBefore;
      this.scrollContainer.scrollTop = scrollBefore + deltaRows * this.lineHeight;
    }
  }

  // Due to how angular orders calls to update ngModel and other inputs on this directive,
  // as well as the ViewChild for the scroll container only being avalaible on afterViewInit
  // (meaning after this directive is inited),
  // the scroll container is not always available
  private isPageScrollContainerAvailable() {
    return this.scrollContainer;
  }

  private canUpdateRows() {
    // when the text area is not displayed yet, for example when the editor is folded,
    // we cannot update this.lineHeight yet. In that case it is best to leave this.ta.rows to its default value.
    // On the other hand, if lineHeight is already set to a positive value, no need to check clientHeight.
    return this.lineHeight > 0 || this.ta.clientHeight > 0;
  }

  // Computes the line height in the text area, the first time that updateRows() is called while this.ta is displayed
  private updateLineHeightIfNull() {
    if (this.lineHeight === null) {
      this.ta.style.padding = '0';
      this.lineHeight = this.ta.clientHeight;
      this.ta.style.padding = '';
    }
  }
}
